import React, { useState, useContext, useMemo, useCallback } from 'react';
import { useQuery } from '@apollo/client';
import { Theme } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import mergeSort, { stringCompare } from '../../../../../../../Utils/mergeSort';

import GetKonteringforslagHistory from './Queries/GetKonteringHistory.gql';
import { FakturaDelQuery_node_TEDLRFakturaDel__Associations_Faktura__Associations_Konteringsforslag_All } from '../../Queries/_QueryTypes/FakturaDelQuery';
import {
  GetKonteringHistory,
  GetKonteringHistoryVariables,
  GetKonteringHistory_node_TEDLRLeverantor__Associations_Fakturor_Connection_edges_node__Associations_Konteringsforslag_All,
} from './Queries/_QueryTypes/GetKonteringHistory';
import {
  TEDLRKonteringsForslagFI_REGISTRERASEnum,
  TEDLRLeverantorfakturaMOMSHANTERINGEnum,
  TEDSelectionOperator,
} from '../../../../../../../globalQueryTypes';
import FakturaDelContext from '../FakturaDelContextManager/FakturaDelContext';
import { AUTOMATICALLY_ADDED_VALUE_PREFIX } from '../FakturaDelContextManager/FakturaDelContextManager';
import {
  KonteringRecept,
  KonteringReceptVariables,
  KonteringRecept_db_LRKonteringsrecept_All,
} from './Queries/_QueryTypes/KonteringRecept';
import { KodplanContext } from '../../../../../../../Components/KodplanManager/Context/KodplanContext';
import konteringReceptQuery from './Queries/KonteringReceptQuery.gql';
import { ActiveDatabaseContext } from '../../../../../../../Components/ActiveDatabaseContext/ActiveDatabaseContext';
import EDTabs from '../../../../../../../BaseComponents/Tabs/EDTabs';
import EDTab from '../../../../../../../BaseComponents/Tabs/Tab/EDTab';
import EDCheckbox from '../../../../../../../BaseComponents/Checkboxes/EDCheckbox';
import EDDialog from '../../../../../../../BaseComponents/Dialog/EDDialog';
import EDAvenyProgress from '../../../../../../../BaseComponents/ProgressIndicators/AvenyProgress/EDAvenyProgress';
import EDTooltip from '../../../../../../../BaseComponents/Tooltip/EDTooltip';
import EDTypography from '../../../../../../../BaseComponents/Typography/EDTypography';
import { BusinessDataFormContext } from '../../../../../_Common/Components/BusinessCollectionComponents/BusinessDataForm/BusinessDataForm';
import { Tillaggsinfokod } from '../Kontering/Components/Tillaggsinfo/Utils/TillaggsinfoUtils';
import KonteringContext from '../../../../../_Common/Components/KonteringManager/KonteringContext';
import AdditionalInfoHistory from './Components/AdditionalinfoHistory/AdditionalinfoHistory';
import KonteringHistory from './Components/KonteringHistory/KonteringHistory';
import {
  additionalInfoResetRefValues,
  additionalInfoResetValues,
  getCalculatedValue,
  getKonteringsforslagValues,
} from './Utils/KonteringHistoryModalUtils';
import {
  getKonteringRadtyp,
  isAutomaticallyAdded,
  isNumberWithinMarginOfError,
} from '../../Utils/FakturadelUtils';
import {
  IsTillaggsInfoActiveQuery,
  IsTillaggsInfoActiveQueryVariables,
} from '../Kontering/Queries/_QueryTypes/IsTillaggsInfoActiveQuery';
import TillaggsInfoQuery from '../Kontering/Queries/IsTillaggsInfoActiveQuery.gql';
import { FormUpdateAssociatonType } from '../../../../../_Common/Components/BusinessCollectionComponents/BusinessDataForm/Utils/BusinessDataFormValueUtils';
import {
  getUtlandskAvdrag,
  getUtlandskMoms,
} from '../Kontering/Utils/KonteringUtils';
import { FakturaattestWrapperContext } from '../../../../Rutiner/FakturaDel/Contexts/FakturaattestWrapperContext';
import { LR_MOMSHANTERING_LEVFAKT_MOMSATSTOLERANS } from '../../Consts/FakturaDelSysoptionConsts';
import { MOMSSATS_TOLERANS_DEFAULT_VALUE } from '../../Consts/FakturadelConsts';

interface IProps {
  open: boolean;
  originKonteringforslag: FakturaDelQuery_node_TEDLRFakturaDel__Associations_Faktura__Associations_Konteringsforslag_All | null;
  setNewBeloppChange: (id: string | null) => void;
  setSelectedOption: (option: boolean) => void;
  supplierID: string;
}

export interface INewKonteringValues {
  BELOPP_INKL_MOMS?: number;
  id: string;
  DEBET: number;
  KREDIT: number;
  SORTERINGS_NR: number;
  AR_BENAMNING?: null;
  AR_AVSKR_TID?: null;
  AR_INV_DATUM?: null;
  AR_TILLGANG_BEN?: null;
  AR_KOMMENTAR?: null;
  AR_NY?: null;
  AR_FI_BENAMNING?: null;
  AR_FI_DATUM?: null;
  AR_FI_KOMMENTAR?: null;
  FI_REGISTRERAS?: null;
  RADTYP: number;
  MOMSPLIKTIG?: boolean;
  VIKT?: number | null;
  ING_MOMS_SATS?: number | null;
  ING_MOMS_MALL?: number | null;
  ING_MOMS_BELOPP?: number | null;
  UTL_MOMS?: number;
  UTL_MOMS_SATS?: number;
  UTL_MOMS_AVDRAG?: number | null;
  _Associations: {
    Faktura_Ref: string;
    IngMoms_Ref?: string | null;
  };
}

const useStyles = makeStyles()((theme: Theme) => ({
  button: { margin: `${theme.spacing(1)} 0` },
  container: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    minHeight: '200px',
    minWidth: '600px',
    [theme.breakpoints.down('sm')]: {
      minWidth: '100%',
    },
  },
  disabledKontering: {
    display: 'flex',
    flexFlow: 'row nowrap',
    alignItems: 'center',
  },
  kontering: {
    display: 'flex',
    flexFlow: 'row nowrap',
    alignItems: 'center',

    '&:hover': {
      cursor: 'pointer',
      border: `1px solid ${theme.customProperties.Fade.DarkOnLight.divider}`,
      borderRadius: '1px',
      margin: '-1px ',
    },
  },

  tabContainer: {
    backgroundColor: theme.customProperties.panelHeadBg.mediumDark,
    color: theme.palette.primary.contrastText,
    boxShadow: theme.shadows[3],
    display: 'flex',
    flex: '0 1 auto',
    justifyContent: 'space-between',
    zIndex: 1,
    [theme.breakpoints.down('sm')]: {
      display: 'flex',
      flexDirection: 'column',
    },
  },
  tabsContentContainer: {
    margin: `${theme.spacing(0)} ${theme.spacing(0.25)}`,
    overflowY: 'auto',
    padding: `${theme.spacing(0.2)} ${theme.spacing(2)}`,
    height: '100%',
    marginBottom: 0,
    [theme.breakpoints.up('md')]: {
      height: theme.spacing(75),
    },
  },
  dialogContentWrapper: {
    margin: 0,
  },
  dialogHeader: {
    zIndex: 2,
  },
  dialogPaper: {
    [theme.breakpoints.up('md')]: {
      maxHeight: 'calc(100% - 16px)',
    },
    [theme.breakpoints.down('lg')]: {
      width: '100%',
    },
  },
  dialogPaperScrollPaper: {},
  dialogContent: {},
}));

const FakturaAttestKonteringHistoryModal: React.FC<
  React.PropsWithChildren<IProps>
> = props => {
  const { classes } = useStyles();
  const fakturaDelContext = useContext(FakturaDelContext);
  const fakturaattestContext = useContext(FakturaattestWrapperContext);
  const { setKontodel, getKostnadsBeloppFormValue } = fakturaDelContext;
  const kodplanContext = useContext(KodplanContext);
  const konteringContext = useContext(KonteringContext);
  const {
    getActiveAttestKontodelDefsByKodplanId,
    getTillaggsinfoAvskrivningKontodelOrdningsnummerList,
  } = konteringContext.konteringUtils;
  const formContext = useContext(BusinessDataFormContext);
  const formContextUpdate = formContext?.update;
  const { activeDatabase } = useContext(ActiveDatabaseContext);
  const [showOthers, setShowOthers] = useState(false);
  const [selectedKonteringar, setSelectedKonteringar] = useState<
    GetKonteringHistory_node_TEDLRLeverantor__Associations_Fakturor_Connection_edges_node__Associations_Konteringsforslag_All[]
  >([]);
  const [selectedRecept, setSelectedRecept] = useState<
    KonteringRecept_db_LRKonteringsrecept_All[]
  >([]);
  const [selectedTab, setSelectedTab] = useState<number>(0);
  const [fetchingMore, setFetchingMore] = useState<boolean>(false);
  const [additionalInfoBillDate, setAdditionalInfoBillDate] =
    useState<string>('');
  const [additionalInfoOpen, setAdditionalInfoOpen] = useState<boolean>(false);
  const [additionalInfo, setAdditionalInfo] =
    useState<GetKonteringHistory_node_TEDLRLeverantor__Associations_Fakturor_Connection_edges_node__Associations_Konteringsforslag_All | null>(
      null
    );
  const [decisionDialogOpen, setDecisionDialogOpen] = useState<boolean>(false);

  const resetStates = useCallback(() => {
    setSelectedKonteringar([]);
    setShowOthers(false);
    props.setSelectedOption(false);
    setDecisionDialogOpen(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const konteringQuery = useQuery<
    GetKonteringHistory,
    GetKonteringHistoryVariables
  >(GetKonteringforslagHistory, {
    variables: {
      id: props.supplierID,
      sign:
        fakturaDelContext.minFakturaAttest.aktuellFakturaAttest
          ?.AttestantSign ?? '',
      op: TEDSelectionOperator.equals,
    },
    fetchPolicy: 'cache-and-network',
  });

  const othersKonteringQuery = useQuery<
    GetKonteringHistory,
    GetKonteringHistoryVariables
  >(GetKonteringforslagHistory, {
    variables: {
      id: props.supplierID,
      sign:
        fakturaDelContext.minFakturaAttest.aktuellFakturaAttest
          ?.AttestantSign ?? '',
      op: TEDSelectionOperator.notequals,
    },
    fetchPolicy: 'cache-and-network',
  });

  const receptQuery = useQuery<KonteringRecept, KonteringReceptVariables>(
    konteringReceptQuery,
    {
      variables: {
        attestantID:
          fakturaDelContext.minFakturaAttest.aktuellFakturaAttest?._Associations
            ?.Attestratt?._Associations?.Attestant?.id ?? '',
        db: activeDatabase ?? '',
        kplanID: `${kodplanContext.activeKodplan?.internalId ?? ''}`,
        leverantorID: props.supplierID,
        omradeID:
          fakturaDelContext.minFakturaAttest.aktuellFakturaAttest?._Associations
            ?.Attestratt?._Associations?.Attestomrade?.id ?? '',
      },
      skip: !kodplanContext.activeKodplan?.internalId || !activeDatabase,
      fetchPolicy: 'cache-and-network',
    }
  );

  const tillaggsInfoQuery = useQuery<
    IsTillaggsInfoActiveQuery,
    IsTillaggsInfoActiveQueryVariables
  >(TillaggsInfoQuery, {
    variables: { dbName: activeDatabase ?? '' },
  });

  const isFIActive = useMemo(
    () =>
      tillaggsInfoQuery.data?.db?.EKTillaggsinfo_All?.find(
        tillaggsInfo => tillaggsInfo.BENAMNING === 'Förbrukningsinventarier'
      )?.AKTIV ?? false,
    [tillaggsInfoQuery.data?.db?.EKTillaggsinfo_All]
  );

  const kontodelDefs = useMemo(
    () =>
      getActiveAttestKontodelDefsByKodplanId(
        fakturaDelContext.kodplanId,
        fakturaDelContext.bokfDatum,
        konteringContext.attestKodbehorigheterUrval ?? [],
        formContext?.formState.currentValues?._Associations?.Faktura
          ?.IngaendeMomsBaskonto,
        fakturaDelContext.isKobFaktura
      ),
    [
      formContext?.formState.currentValues?._Associations?.Faktura
        ?.IngaendeMomsBaskonto,
      getActiveAttestKontodelDefsByKodplanId,
      konteringContext.attestKodbehorigheterUrval,
      fakturaDelContext.bokfDatum,
      fakturaDelContext.isKobFaktura,
      fakturaDelContext.kodplanId,
    ]
  );

  const writeoffKontodelOrdernumber = useMemo(
    () =>
      getTillaggsinfoAvskrivningKontodelOrdningsnummerList(
        fakturaDelContext.bokfDatum
      ),
    [
      fakturaDelContext.bokfDatum,
      getTillaggsinfoAvskrivningKontodelOrdningsnummerList,
    ]
  );

  const periodiseringBeloppEnhet: number = useMemo(() => {
    return fakturaattestContext.sysPrisbasbelopp?.find(sysbasbelopp => {
      const sysbasbeloppDate = new Date(sysbasbelopp.BOKF_AR);
      return (
        sysbasbeloppDate.getFullYear() ===
        fakturaDelContext.bokfDatum.getFullYear()
      );
    })?.BeloppEnhet;
  }, [fakturaDelContext.bokfDatum, fakturaattestContext.sysPrisbasbelopp]);

  const isFakturaBeloppLowerThanPeriodiseringBeloppEnhet = useMemo(() => {
    return (
      formContext?.parentFormContext?.formState?.currentValues?.FakturaBelopp2 <
      periodiseringBeloppEnhet
    );
  }, [
    formContext?.parentFormContext?.formState?.currentValues?.FakturaBelopp2,
    periodiseringBeloppEnhet,
  ]);

  const writeoffKontodelDefs = writeoffKontodelOrdernumber.map(
    avskrivningskontoOrdningsnummer => {
      const kontodel = kontodelDefs.find(
        kontodel => kontodel.ordningsNr === avskrivningskontoOrdningsnummer
      );

      if (!kontodel) {
        throw new Error(
          `Kunde inte hitta kontodel med ordningsnummer ${avskrivningskontoOrdningsnummer}`
        );
      }

      return kontodel;
    }
  );

  const receptData = useMemo(
    () =>
      mergeSort(receptQuery.data?.db?.LRKonteringsrecept_All ?? [], (a, b) =>
        stringCompare(a.BENAMNING, b.BENAMNING)
      ),
    [receptQuery]
  );

  const sysMomsTolerans = useMemo(() => {
    return fakturaattestContext.sysOptions?.find(
      sysopt => sysopt.OptionKey === LR_MOMSHANTERING_LEVFAKT_MOMSATSTOLERANS
    );
  }, [fakturaattestContext.sysOptions]);

  const isUseButtonDisabled = (): boolean => {
    return selectedKonteringar.length === 0 && selectedRecept.length === 0;
  };

  const setKonteringforslagValues = useCallback(
    (
      calculatedAmount: string,
      isAdded: boolean,
      ignoreFormHasChanged: boolean,
      values: INewKonteringValues,
      associatedObject: FormUpdateAssociatonType[],
      isKontodelMomspliktig: boolean,
      skipSetBelopp: boolean
    ) => {
      fakturaDelContext.addOrUpdateKonteringsforslag(
        values,
        isAdded,
        associatedObject,
        ignoreFormHasChanged
      );
      if (!isKontodelMomspliktig && !skipSetBelopp) {
        fakturaDelContext.setBelopp(
          values.id,
          calculatedAmount,
          isAdded,
          false,
          props.setNewBeloppChange,
          undefined
        );
      }
    },
    [fakturaDelContext, props.setNewBeloppChange]
  );

  const addAdditionalInfo = useCallback(
    (values: any) => {
      formContextUpdate?.([{ operation: 'OwnFields', values: values }]);
    },
    [formContextUpdate]
  );

  const addAdditionalInfoRefs = useCallback(
    (ref: any[]) => {
      formContextUpdate?.([
        {
          operation: 'OwnFields',
        },
        ...ref,
      ]);
    },
    [formContextUpdate]
  );

  const onClick = useCallback(
    (useAdditionalInfo: boolean) => {
      const allKonteringar = fakturaDelContext.konteringsforslag;
      const originKontering = props.originKonteringforslag;
      if (originKontering == null) {
        return;
      }

      const utlLevMomsSats =
        formContext?.parentFormContext?.formState.currentValues?._Associations
          ?.Faktura?._Associations?.Leverantor?._Associations?.UtlandskMoms
          ?.UTL_MOMS_SATS;

      if (selectedKonteringar.length !== 0) {
        selectedKonteringar.forEach((kontering, index) => {
          const updateExisting =
            index === 0 && !isAutomaticallyAdded(originKontering);

          index = index + allKonteringar.length;

          const newSortNumber =
            originKontering.SORTERINGS_NR ??
            fakturaDelContext.konteringsforslag.length + 1;

          const momsHantering: TEDLRLeverantorfakturaMOMSHANTERINGEnum | null =
            formContext?.parentFormContext?.formState.currentValues
              ?._Associations?.Faktura?.MOMSHANTERING;
          const registreratMomsBelopp =
            formContext?.parentFormContext?.formState.currentValues
              ?._Associations?.Faktura?.MomsBelopp2;

          const selectedMomskontodelsDefOrdningsNr = kontodelDefs.find(
            kontodel => kontodel.anvandsForMoms
          )?.ordningsNr;

          const utlandsMomsMallId =
            formContext?.parentFormContext?.formState?.currentValues
              ?._Associations?.Faktura?._Associations?.Leverantor?._Associations
              ?.UtlandskMoms?.id;

          // Hämta ut en default radtyp, hårdkodat momspliktigVerksamhet till false eftersom radtypen räknas om när man har markerad på koddel.
          // Om systemet istället är markerad på leverantören så spelar momspliktigVerksamhet ingen roll
          const radtypDefault = getKonteringRadtyp(
            momsHantering,
            registreratMomsBelopp,
            false
          );

          let values: INewKonteringValues = {
            id: originKontering.id ?? AUTOMATICALLY_ADDED_VALUE_PREFIX + index,
            DEBET: 0,
            KREDIT: 0,
            SORTERINGS_NR: newSortNumber,
            RADTYP: radtypDefault,
            ...additionalInfoResetValues,
            _Associations: {
              Faktura_Ref: fakturaDelContext.fakturaId,
              ...additionalInfoResetRefValues,
            },
          };

          const associatedObject: FormUpdateAssociatonType[] = [];
          let isKontodelMomspliktig = false;
          let skipSetBelopp = false;
          if (
            selectedMomskontodelsDefOrdningsNr &&
            momsHantering !==
              TEDLRLeverantorfakturaMOMSHANTERINGEnum.EJ_HANTERAT
          ) {
            const kalkMomssats =
              formContext?.parentFormContext?.formState?.currentValues
                ?._Associations?.Faktura?.KalkMomssats ?? 0;
            const preSelectedMomssats = fakturaattestContext.sysMoms?.find(
              sysmoms =>
                isNumberWithinMarginOfError(
                  sysmoms.MOMS_SATS1,
                  sysMomsTolerans?.OptionData ??
                    MOMSSATS_TOLERANS_DEFAULT_VALUE,
                  kalkMomssats
                )
            );

            let newUtlMoms = originKontering.UTL_MOMS;

            if (utlandsMomsMallId && originKontering.UTL_MOMS_SATS == null) {
              //Om det inte har funnit en utlandsk momssats tidigare så räknar vi ut momsen utifrån
              // momssatsen från leverantören
              newUtlMoms = getUtlandskMoms(
                originKontering.BELOPP_EXKL_MOMS,
                utlLevMomsSats
              );
            }

            const konteringsforslagsValue = getKonteringsforslagValues(
              originKontering.id ?? AUTOMATICALLY_ADDED_VALUE_PREFIX + index,
              newSortNumber,
              radtypDefault,
              selectedMomskontodelsDefOrdningsNr,
              (
                kontering?._Associations as unknown as
                  | Record<string, { id?: string }>
                  | undefined
              )?.[`Kontodel${selectedMomskontodelsDefOrdningsNr}`]?.id,
              momsHantering,
              registreratMomsBelopp,
              originKontering.ING_MOMS_SATS,
              originKontering.id ?? undefined,
              originKontering.DEBET,
              originKontering.KREDIT,
              originKontering.BELOPP_INKL_MOMS,
              originKontering.BELOPP_EXKL_MOMS,
              originKontering.UTL_MOMS_SATS,
              newUtlMoms,
              originKontering.ING_MOMS_MALL ?? null,
              utlLevMomsSats ?? null,
              fakturaDelContext.fakturaId,
              fakturaDelContext.kodplanId,
              fakturaDelContext.bokfDatum,
              preSelectedMomssats,
              props.setNewBeloppChange,
              getKostnadsBeloppFormValue,
              konteringContext.konteringUtils.isNewSelectedKontodelMomspliktig,
              konteringContext.konteringUtils.getAvdragsratt
            );

            values = { ...values, ...konteringsforslagsValue.values };
            associatedObject.push(...konteringsforslagsValue.associatedObject);
            isKontodelMomspliktig = values.MOMSPLIKTIG ?? false;
          }

          const calculatedAmount = getCalculatedValue(
            originKontering.RADTYP,
            originKontering.DEBET,
            originKontering.KREDIT,
            originKontering.BELOPP_EXKL_MOMS,
            originKontering.BELOPP_INKL_MOMS,
            selectedKonteringar.length
          );

          const radtyp = getKonteringRadtyp(
            momsHantering,
            registreratMomsBelopp,
            isKontodelMomspliktig
          );

          if (radtyp === 5 || radtyp === 6) {
            if (utlandsMomsMallId && originKontering.UTL_MOMS_SATS == null) {
              associatedObject.push({
                associationName: 'UtlandsMomsMall',
                operation: 'Ref',
                id: utlandsMomsMallId,
                values: {
                  id: utlandsMomsMallId,
                },
              });

              values.UTL_MOMS = getUtlandskMoms(
                originKontering.BELOPP_EXKL_MOMS,
                utlLevMomsSats
              );

              values.UTL_MOMS_SATS = utlLevMomsSats;

              if (radtyp === 6) {
                values.UTL_MOMS_AVDRAG = getUtlandskAvdrag(
                  originKontering.BELOPP_EXKL_MOMS,
                  utlLevMomsSats,
                  1
                );
                // Vikten är alltid 1 för radtyp 6
                values.VIKT = 1;
              }
            }

            if (!isKontodelMomspliktig) {
              const updatedKostandsBeloppValue = getKostnadsBeloppFormValue(
                originKontering.id ?? '',
                originKontering.DEBET,
                originKontering.KREDIT,
                props.setNewBeloppChange,
                radtyp,
                originKontering.BELOPP_INKL_MOMS,
                originKontering.BELOPP_EXKL_MOMS,
                originKontering.ING_MOMS_SATS,
                radtyp === 6 ? 1 : 0,
                null,
                originKontering?.UTL_MOMS_SATS,
                originKontering.UTL_MOMS
              );

              values = { ...values, ...updatedKostandsBeloppValue };
              skipSetBelopp = true;
            }
          }

          if (updateExisting) {
            setKonteringforslagValues(
              calculatedAmount,
              false,
              false,
              values,
              associatedObject,
              isKontodelMomspliktig,
              skipSetBelopp
            );
          } else {
            setKonteringforslagValues(
              calculatedAmount,
              true,
              false,
              values,
              associatedObject,
              isKontodelMomspliktig,
              skipSetBelopp
            );
          }

          if (kontering._Associations != null) {
            for (let i = 0; i < kontodelDefs.length; i++) {
              const kontodel = (
                kontering._Associations as unknown as Record<
                  string,
                  { KONTODEL?: string }
                >
              )[`Kontodel${i + 1}`]?.KONTODEL;
              setKontodel(
                values.id,
                kontodel ?? '',
                i + 1,
                fakturaDelContext.kodplanId,
                fakturaDelContext.bokfDatum,
                isAutomaticallyAdded(values),
                isFIActive,
                isFakturaBeloppLowerThanPeriodiseringBeloppEnhet
              );
            }
          }

          if (useAdditionalInfo) {
            if (
              kontering?.TILLAGGSINFO_KOD === Tillaggsinfokod.FI &&
              kontering?.FI_REGISTRERAS ===
                TEDLRKonteringsForslagFI_REGISTRERASEnum.JA
            ) {
              const additionalInfoValues = {
                AR_FI_BENAMNING: kontering.AR_FI_BENAMNING,
                AR_FI_DATUM: kontering.AR_FI_DATUM,
                AR_FI_KOMMENTAR: kontering.AR_FI_KOMMENTAR,
                FI_REGISTRERAS: kontering.FI_REGISTRERAS,
              };

              addAdditionalInfo(additionalInfoValues);
              if (kontering._Associations?.FIOmrade?.id) {
                addAdditionalInfoRefs([
                  {
                    operation: 'Ref',
                    associationName: 'FIOmrade',
                    values: {
                      id: kontering._Associations?.FIOmrade?.id,
                    },
                  },
                ]);
              }
              if (
                kontering.AR_FI_OMRADE_ID &&
                kontering._Associations?.FIByggnad?.id
              ) {
                addAdditionalInfoRefs([
                  {
                    operation: 'Ref',
                    associationName: 'FIByggnad',
                    values: {
                      id: kontering._Associations?.FIByggnad?.id,
                    },
                  },
                ]);
              }
              if (kontering._Associations?.FIKategori?.id) {
                addAdditionalInfoRefs([
                  {
                    operation: 'Ref',
                    associationName: 'FIKategori',
                    values: kontering._Associations?.FIKategori?.id
                      ? {
                          id: kontering._Associations?.FIKategori?.id,
                          AR_ANLKAT_ID: kontering?.AR_FI_ANLKAT_ID,
                        }
                      : null,
                  },
                ]);
              }
            } else if (
              kontering?.TILLAGGSINFO_KOD != null &&
              kontering?.TILLAGGSINFO_KOD !== Tillaggsinfokod.FI
            ) {
              const additionalInfoValues = {
                AR_BENAMNING: kontering.AR_BENAMNING,
                AR_AVSKR_TID: kontering.AR_AVSKR_TID,
                AR_INV_DATUM: kontering.AR_INV_DATUM,
                AR_TILLGANG_BEN: kontering.AR_TILLGANG_BEN,
                AR_KOMMENTAR: kontering.AR_KOMMENTAR,
                AR_NY: kontering.AR_NY,
              };
              addAdditionalInfo(additionalInfoValues);
              if (kontering?._Associations?.Komponenttyp?.id) {
                addAdditionalInfoRefs([
                  {
                    operation: 'Ref',
                    associationName: 'Komponenttyp',
                    values: {
                      id: kontering?._Associations?.Komponenttyp?.id,
                    },
                  },
                ]);
              }
              if (
                kontering?._Associations?.Tillgang?.id &&
                kontering?._Associations?.Tillgang?.RuleID
              ) {
                addAdditionalInfoRefs([
                  {
                    operation: 'Ref',
                    associationName: 'Tillgang',
                    values: {
                      id: kontering?._Associations?.Tillgang?.id,
                      RuleID: kontering?._Associations?.Tillgang?.RuleID,
                    },
                  },
                  {
                    operation: 'Values',
                    associationName: 'Plats',
                    values: {
                      id: kontering?._Associations?.Tillgang?._Associations
                        ?.Plats?.id,
                      PLATS_KOD:
                        kontering?._Associations?.Tillgang?._Associations?.Plats
                          ?.PLATS_KOD,
                      BENAMNING:
                        kontering?._Associations?.Tillgang?._Associations?.Plats
                          ?.BENAMNING,
                    },
                  },
                ]);
              }
              if (kontering._Associations?.Omrade?.id) {
                addAdditionalInfoRefs([
                  {
                    operation: 'Ref',
                    associationName: 'Omrade',
                    values: {
                      id: kontering._Associations?.Omrade?.id,
                    },
                  },
                ]);
              }
              writeoffKontodelDefs.forEach(writOffDef => {
                const savedWriteoffValues = (
                  kontering._Associations as unknown as
                    | Record<
                        string,
                        { id?: string; KONTODEL?: string; BENAMNING?: string }
                      >
                    | undefined
                )?.['AvskrKontodel' + writOffDef.ordningsNr];
                if (savedWriteoffValues?.id) {
                  addAdditionalInfoRefs([
                    {
                      operation: 'Ref',
                      associationName: `AvskrKontodel${writOffDef.ordningsNr}`,
                      values: savedWriteoffValues?.id
                        ? {
                            id: savedWriteoffValues.id,
                            KONTODEL: savedWriteoffValues.KONTODEL,
                            BENAMNING: savedWriteoffValues.BENAMNING,
                          }
                        : null,
                    },
                  ]);
                }
              });
            }
          }
        });
      }

      if (selectedRecept.length !== 0) {
        selectedRecept.forEach((recept, index) => {
          const updateExisting =
            index === 0 && !isAutomaticallyAdded(originKontering);

          index = index + allKonteringar.length;

          const newSortNumber =
            originKontering.SORTERINGS_NR ??
            fakturaDelContext.konteringsforslag.length + 1;

          const momsHantering: TEDLRLeverantorfakturaMOMSHANTERINGEnum | null =
            formContext?.parentFormContext?.formState.currentValues
              ?._Associations?.Faktura?.MOMSHANTERING;
          const registreratMomsBelopp =
            formContext?.parentFormContext?.formState.currentValues
              ?._Associations?.Faktura?.MomsBelopp2;

          const radtyp = getKonteringRadtyp(
            momsHantering,
            registreratMomsBelopp,
            false
          );

          let values: INewKonteringValues = {
            id: originKontering.id ?? AUTOMATICALLY_ADDED_VALUE_PREFIX + index,
            DEBET: 0,
            KREDIT: 0,
            RADTYP: radtyp,
            SORTERINGS_NR: newSortNumber,
            _Associations: { Faktura_Ref: fakturaDelContext.fakturaId },
          };

          const selectedMomskontodelsDefOrdningsNr = kontodelDefs.find(
            kontodel => kontodel.anvandsForMoms
          )?.ordningsNr;

          const utlandsMomsMallId =
            formContext?.parentFormContext?.formState?.currentValues
              ?._Associations?.Faktura?._Associations?.Leverantor?._Associations
              ?.UtlandskMoms?.id;

          let isKontodelMomspliktig = false;
          let skipSetBelopp = false;

          if (
            selectedMomskontodelsDefOrdningsNr &&
            momsHantering !==
              TEDLRLeverantorfakturaMOMSHANTERINGEnum.EJ_HANTERAT
          ) {
            const receptKontodel =
              (recept as unknown as Record<string, string> | undefined)?.[
                `KONTODEL_${selectedMomskontodelsDefOrdningsNr}`
              ] ?? '';
            isKontodelMomspliktig =
              konteringContext.konteringUtils.isNewSelectedKontodelMomspliktig(
                fakturaDelContext.kodplanId,
                selectedMomskontodelsDefOrdningsNr,
                receptKontodel,
                fakturaDelContext.bokfDatum,
                true
              );

            const radtyp = getKonteringRadtyp(
              momsHantering,
              registreratMomsBelopp,
              isKontodelMomspliktig
            );

            values.RADTYP = radtyp;

            if (isKontodelMomspliktig) {
              const newAvdragsratt =
                konteringContext.konteringUtils.getAvdragsratt(
                  fakturaDelContext.kodplanId,
                  selectedMomskontodelsDefOrdningsNr,
                  receptKontodel,
                  fakturaDelContext.bokfDatum,
                  radtyp,
                  true
                );
              values.MOMSPLIKTIG = isKontodelMomspliktig;
              values.VIKT = isKontodelMomspliktig ? newAvdragsratt : null;

              if (radtyp === 5) {
                values.UTL_MOMS_AVDRAG = getUtlandskAvdrag(
                  originKontering.BELOPP_EXKL_MOMS,
                  originKontering.UTL_MOMS_SATS == null
                    ? utlLevMomsSats
                    : originKontering.UTL_MOMS_SATS,
                  newAvdragsratt
                );
              }
            } else {
              //Nollställ momsvärden
              values.VIKT = null;
              values.ING_MOMS_MALL = null;
              values.ING_MOMS_SATS = null;
              values.ING_MOMS_BELOPP = null;
              values.MOMSPLIKTIG = false;
              values._Associations.IngMoms_Ref = null;
            }
          }

          const calculatedAmount = getCalculatedValue(
            originKontering.RADTYP,
            originKontering.DEBET,
            originKontering.KREDIT,
            originKontering.BELOPP_EXKL_MOMS,
            originKontering.BELOPP_INKL_MOMS,
            selectedRecept.length
          );

          const associatedObject: FormUpdateAssociatonType[] = [];
          if ((radtyp === 5 || radtyp === 6) && utlandsMomsMallId) {
            associatedObject.push({
              associationName: 'UtlandsMomsMall',
              operation: 'Ref',
              id: utlandsMomsMallId,
              values: {
                id: utlandsMomsMallId,
              },
            });
          }

          if (radtyp === 5 || radtyp === 6) {
            if (utlandsMomsMallId && originKontering.UTL_MOMS_SATS == null) {
              associatedObject.push({
                associationName: 'UtlandsMomsMall',
                operation: 'Ref',
                id: utlandsMomsMallId,
                values: {
                  id: utlandsMomsMallId,
                },
              });

              if (radtyp === 6) {
                values.UTL_MOMS_AVDRAG = getUtlandskAvdrag(
                  originKontering.BELOPP_EXKL_MOMS,
                  utlLevMomsSats,
                  1
                );
                // Vikten är alltid 1 för radtyp 6
                values.VIKT = 1;
              }
            }

            if (!isKontodelMomspliktig) {
              const updatedKostandsBeloppValue = getKostnadsBeloppFormValue(
                originKontering.id ?? '',
                originKontering.DEBET,
                originKontering.KREDIT,
                props.setNewBeloppChange,
                radtyp,
                originKontering.BELOPP_INKL_MOMS,
                originKontering.BELOPP_EXKL_MOMS,
                originKontering.ING_MOMS_SATS,
                radtyp === 6 ? 1 : 0,
                null,
                originKontering?.UTL_MOMS_SATS,
                originKontering.UTL_MOMS
              );

              values = { ...values, ...updatedKostandsBeloppValue };
              skipSetBelopp = true;
            }
          }

          if (updateExisting) {
            setKonteringforslagValues(
              calculatedAmount,
              false,
              false,
              values,
              associatedObject,
              isKontodelMomspliktig,
              skipSetBelopp
            );
          } else {
            setKonteringforslagValues(
              calculatedAmount,
              true,
              false,
              values,
              associatedObject,
              isKontodelMomspliktig,
              skipSetBelopp
            );
          }

          for (let i = 0; i < kontodelDefs.length; i++) {
            setKontodel(
              values.id,
              (recept as unknown as Record<string, string>)[
                `KONTODEL_${i + 1}`
              ],
              i + 1,
              fakturaDelContext.kodplanId,
              fakturaDelContext.bokfDatum,
              isAutomaticallyAdded(values),
              isFIActive,
              isFakturaBeloppLowerThanPeriodiseringBeloppEnhet
            );
          }
        });
      }
    },
    [
      fakturaDelContext.konteringsforslag,
      fakturaDelContext.fakturaId,
      fakturaDelContext.kodplanId,
      fakturaDelContext.bokfDatum,
      props.originKonteringforslag,
      props.setNewBeloppChange,
      selectedKonteringar,
      selectedRecept,
      formContext?.parentFormContext?.formState.currentValues?._Associations
        ?.Faktura?.MOMSHANTERING,
      formContext?.parentFormContext?.formState.currentValues?._Associations
        ?.Faktura?.MomsBelopp2,
      formContext?.parentFormContext?.formState.currentValues?._Associations
        ?.Faktura?._Associations?.Leverantor?._Associations?.UtlandskMoms?.id,
      formContext?.parentFormContext?.formState.currentValues?._Associations
        ?.Faktura?._Associations?.Leverantor?._Associations?.UtlandskMoms
        ?.UTL_MOMS_SATS,
      formContext?.parentFormContext?.formState.currentValues?._Associations
        ?.Faktura?.KalkMomssats,
      kontodelDefs,
      konteringContext.konteringUtils,
      fakturaattestContext.sysMoms,
      getKostnadsBeloppFormValue,
      sysMomsTolerans?.OptionData,
      setKonteringforslagValues,
      setKontodel,
      isFIActive,
      isFakturaBeloppLowerThanPeriodiseringBeloppEnhet,
      addAdditionalInfo,
      addAdditionalInfoRefs,
      writeoffKontodelDefs,
    ]
  );

  return (
    <EDDialog
      classes={{
        contentWrapper: classes.dialogContentWrapper,
        paper: classes.dialogPaper,
        paperScrollPaper: classes.dialogPaperScrollPaper,
        content: classes.dialogContent,
        dialogHeader: classes.dialogHeader,
      }}
      footerActions={[
        {
          label: 'Avbryt',
          onClick: () => {
            resetStates();
          },
        },
        {
          label: 'Använd',
          buttonProps: {
            disabled: isUseButtonDisabled(),
          },

          onClick: () => {
            if (selectedKonteringar.length > 0 || selectedRecept.length > 0) {
              if (selectedKonteringar?.[0]?.TILLAGGSINFO_KOD != null) {
                if (
                  selectedKonteringar?.[0]?.TILLAGGSINFO_KOD ===
                    Tillaggsinfokod.FI &&
                  isFIActive &&
                  selectedKonteringar?.[0]?.FI_REGISTRERAS ===
                    TEDLRKonteringsForslagFI_REGISTRERASEnum.JA
                ) {
                  setDecisionDialogOpen(true);
                } else if (
                  selectedKonteringar?.[0]?.TILLAGGSINFO_KOD !==
                  Tillaggsinfokod.FI
                ) {
                  setDecisionDialogOpen(true);
                } else {
                  onClick(false);
                  resetStates();
                }
              } else {
                onClick(false);
                resetStates();
              }
            }
          },
        },
      ]}
      open={props.open}
      onClose={() => {
        resetStates();
      }}
      title="Konteringförslag"
    >
      {fetchingMore && <EDAvenyProgress />}
      <div className={classes.container}>
        {receptQuery.data && receptData && receptData.length > 0 && (
          <div className={classes.tabContainer}>
            <EDTabs
              value={selectedTab}
              onChange={selectedTabIndex => {
                setSelectedTab(selectedTabIndex);
              }}
              indicatorColor="white"
              noBoxShadow
            >
              <EDTab label="Historik" disabled={false} spaciousPadding />
              <EDTab label="Sparade förslag" disabled={false} spaciousPadding />
            </EDTabs>
          </div>
        )}
        <div>
          {selectedTab === 0 && (
            <KonteringHistory
              fetchingMore={fetchingMore}
              konteringQuery={konteringQuery}
              othersKonteringQuery={othersKonteringQuery}
              selectedKonteringar={selectedKonteringar}
              selectedRecept={selectedRecept}
              setAdditionalInfo={setAdditionalInfo}
              setAdditionalInfoBillDate={setAdditionalInfoBillDate}
              setAdditionalInfoOpen={setAdditionalInfoOpen}
              setFetchingMore={setFetchingMore}
              setSelectedKonteringar={setSelectedKonteringar}
              setShowOthers={setShowOthers}
              showOthers={showOthers}
              supplierID={props.supplierID}
            />
          )}
          {selectedTab === 1 && (
            <div className={classes.tabsContentContainer}>
              {receptQuery.data && konteringQuery.data && receptData ? (
                <>
                  <EDTypography variant="h6" bold>
                    Mina sparade förslag
                  </EDTypography>
                  {receptData.length > 0 ? (
                    receptData.map((recept, index) => {
                      const disableRecept =
                        (selectedRecept.length !== 0 &&
                          !selectedRecept.includes(recept)) ||
                        selectedKonteringar.length !== 0;

                      return (
                        <EDTooltip
                          enterDelay={500}
                          enterTouchDelay={500}
                          key={index}
                          title={
                            disableRecept
                              ? 'Man kan bara välja ett konteringsförslag'
                              : ''
                          }
                          enableClickOnTouch={disableRecept}
                        >
                          <div
                            className={
                              disableRecept
                                ? classes.disabledKontering
                                : classes.kontering
                            }
                            onClick={() => {
                              if (disableRecept) {
                                return;
                              }
                              const selectedReceptCopy = selectedRecept;
                              if (selectedRecept.includes(recept)) {
                                setSelectedRecept(
                                  selectedReceptCopy.filter(
                                    filterRecept =>
                                      filterRecept.id !== recept.id
                                  )
                                );
                              } else {
                                setSelectedRecept([...selectedRecept, recept]);
                              }
                            }}
                          >
                            <EDCheckbox
                              checked={selectedRecept.includes(recept)}
                              marginRight={2}
                              disabled={disableRecept}
                              onCheck={() => {
                                const selectedReceptCopy = selectedRecept;
                                if (selectedRecept.includes(recept)) {
                                  setSelectedRecept(
                                    selectedReceptCopy.filter(
                                      filterRecept =>
                                        filterRecept.id !== recept.id
                                    )
                                  );
                                } else {
                                  setSelectedRecept([
                                    ...selectedRecept,
                                    recept,
                                  ]);
                                }
                              }}
                            />
                            <span style={{ width: '100%' }}>
                              <EDTypography
                                variant="body1"
                                bold
                                color={
                                  disableRecept
                                    ? 'textSecondary'
                                    : 'textPrimary'
                                }
                              >
                                {recept.BENAMNING}
                              </EDTypography>
                              <EDTypography
                                variant="body2"
                                bold
                                color={
                                  disableRecept
                                    ? 'textSecondary'
                                    : 'textPrimary'
                                }
                                style={{ fontSize: '0.625rem' }}
                              >
                                {recept.Kontostrang?.replaceAll('-', ' - ')}
                              </EDTypography>
                              <EDTypography
                                variant="body2"
                                bold
                                color={
                                  disableRecept
                                    ? 'textSecondary'
                                    : 'textPrimary'
                                }
                                style={{ fontSize: '0.625rem' }}
                              >
                                {recept.Kontoben?.replaceAll('-', ' - ')}
                              </EDTypography>
                            </span>
                          </div>
                        </EDTooltip>
                      );
                    })
                  ) : (
                    <EDTypography>Du har inga sparade förslag</EDTypography>
                  )}
                </>
              ) : (
                <EDAvenyProgress />
              )}
            </div>
          )}
        </div>
      </div>
      <EDDialog
        onClose={() => {
          setAdditionalInfoOpen(false);
        }}
        onExited={() => {
          setAdditionalInfo(null);
          setAdditionalInfoBillDate('');
        }}
        open={additionalInfoOpen}
        title={'Tilläggsinformation för kontering'}
      >
        <AdditionalInfoHistory
          additionalInfo={additionalInfo}
          billDate={additionalInfoBillDate}
        />
      </EDDialog>
      <EDDialog
        onClose={() => {
          setDecisionDialogOpen(false);
        }}
        noCloseIcon
        open={decisionDialogOpen}
        title={'Vill du återanvända tilläggsinformation?'}
        footerActions={[
          { label: 'Avbryt', onClick: () => setDecisionDialogOpen(false) },
          {
            label: 'Nej',
            onClick: () => {
              onClick(false);
              resetStates();
            },
          },
          {
            label: 'JA',
            onClick: () => {
              onClick(true);
              resetStates();
            },
          },
        ]}
      >
        <EDTypography>
          Denna raden innehåller tilläggsinformation, vill du återanvända
          informationen?
        </EDTypography>
      </EDDialog>
    </EDDialog>
  );
};

export default FakturaAttestKonteringHistoryModal;
